import { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../../../contexts-hooks/hooks/useToster";
import {
  useEditUserInformation,
  useUserById,
} from "../../../../store/query/userQuery";
import { isValidPhoneNumber } from "libphonenumber-js";

export const useEditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [phomeErrorMessage, setPhomeErrorMessage] = useState(null);
  const [validPhoneNumber, setValidPhoneNumber] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: recruiterData, isLoading, isError } = useUserById(id);
  const editUserMutation = useEditUserInformation();

  const roles = [
    { value: "recruiter", label: "Recruiter" },
    { value: "hiring manager", label: "Hiring Manager" },
  ];

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      role: "",
      title: "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Name is required")
        .max(50, "Name should not exceed 50 characters"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      phone: Yup.string().required("Phone number is required"),
      role: Yup.string().required("Role is required"),
      title: Yup.string().required("Title is required"),
    }),
    onSubmit: (values) => {
      if (!hasChanges) {
        showToast("No changes detected", "warning");
        return;
      }
      setIsSubmitting(true);

      editUserMutation.mutate(
        { id, ...values },
        {
          onSuccess: (data) => {
            setIsSubmitting(false);
            showToast("Information updated successfully!");
            navigate("/app/settings/recruiter-list");
          },
          onError: (error) => {
            console.error("Update error:", error);
            setIsSubmitting(false);
            const errorMessage =
              error?.response?.data?.message || "Failed to update recruiter";
            showToast(errorMessage, "error");
          },
        }
      );
    },
  });

  useEffect(() => {
    if (initialFormValues) {
      const formChanged = Object.keys(initialFormValues).some(
        (key) => initialFormValues[key] !== formik.values[key]
      );
      setHasChanges(formChanged);
    }
  }, [formik.values, initialFormValues]);

  useEffect(() => {
    if (recruiterData?.data) {
      const recruiter = recruiterData?.data?.data;
      const values = {
        name: recruiter.name || "",
        email: recruiter.email || "",
        phone: recruiter.phone || "",
        role: recruiter.role || "",
        title: recruiter.title || "",
      };

      formik.setValues(values);
      setInitialFormValues(values);
    }
  }, [recruiterData]);

  const handleNavigateToList = () => {
    navigate("/app/settings/recruiter-list");
  };

  const validatePhoneNumber = (phone, country) => {
    const phoneNumber = phone.slice(country.dialCode.length);
    formik.setFieldValue(
      "candidatePhone",
      "+" + country.dialCode + " " + phoneNumber
    );
    if (phone.length === 0) {
      setPhomeErrorMessage("Phone number is required");
      return;
    }
    const isValid = isValidPhoneNumber(
      "+" + country.dialCode + " " + phoneNumber,
      country.countryCode.toUpperCase()
    );
    if (!isValid) {
      setValidPhoneNumber(false);
      setPhomeErrorMessage("Invalid phone number");
    } else {
      setPhomeErrorMessage(null);
      setValidPhoneNumber(true);
    }
  };

  const isLoading_combined =
    isLoading || isSubmitting || editUserMutation.isLoading;

  return {
    formik,
    roles,
    isLoading,
    isError,
    phomeErrorMessage,
    validatePhoneNumber,
    handleNavigateToList,
    editUserMutation,
    hasChanges,
    isSubmitting,
    isLoading_combined,
    validPhoneNumber,
  };
};
